import { parseSourceId } from "@/store/scoringStore";
import type { CompanyFilters } from "../../types";
import {
  defaultModelScoreSlice,
  hasCriteria,
  type ModelScoreSlice,
} from "./slice";

export function toCompanyFilters(
  slice: ModelScoreSlice
): Partial<CompanyFilters> {
  // Criteria-less entries are model SELECTION (mirrored from / synced to the
  // active scoring sources on confirm) — they don't filter anything, so they
  // stay out of the composed filters: selection alone must not read as a
  // pending/applied filter change, and activation persists via
  // scoringSourceIds, not here.
  const entries = slice.entries.filter(hasCriteria);
  if (entries.length === 0) return {};
  // UI-only carrier: stripped before `filters` is sent to the API, then turned
  // into the top-level `modelScoreFilter` arg by buildModelScoreFilter.
  return { modelScoreFilterUi: { entries } };
}

export function fromCompanyFilters(filters: CompanyFilters): ModelScoreSlice {
  const ui = filters.modelScoreFilterUi;
  if (!ui) return defaultModelScoreSlice;
  if (ui.entries) {
    return { entries: ui.entries.filter((e) => Boolean(e.modelId)) };
  }
  // Legacy model-agnostic shape (pre RGI-2348): the criteria applied to "the
  // single active V2 model", which segment saves stamp into scoringSourceIds —
  // bind the criteria to that model, or drop them when no v2 source was saved.
  const legacy = { modelId: "", composite: ui.composite, tiers: ui.tiers };
  if (!hasCriteria(legacy)) return defaultModelScoreSlice;
  const family = (filters.scoringSourceIds ?? [])
    .map(parseSourceId)
    .find((p): p is { kind: "v2"; family: string } => p?.kind === "v2")?.family;
  return family
    ? { entries: [{ ...legacy, modelId: family }] }
    : defaultModelScoreSlice;
}
